<template>
  <div class="mv-wrap" ref="wrap">
    <v-row>
      <!-- Mission -->
      <v-col cols="12" md="6">
        <div
          class="mv-card mv-card--mission"
          :class="{ 'is-visible': inView }"
          style="transition-delay: 0s"
        >
          <!-- Top accent bar -->
          <div class="mv-bar mv-bar--mission" aria-hidden="true"></div>

          <div class="mv-card-inner">
            <!-- Icon -->
            <div class="mv-icon mv-icon--mission" aria-hidden="true">
              <v-icon size="28" color="white">mdi-target</v-icon>
            </div>

            <!-- Label -->
            <p class="mv-label mv-label--mission">{{ $t('about.ourMission') }}</p>

            <!-- Headline -->
            <h3 class="mv-title">{{ $t('about.missionDesc') }}</h3>

            <!-- Divider -->
            <div class="mv-divider mv-divider--mission" aria-hidden="true"></div>

            <!-- Points -->
            <ul class="mv-points" role="list">
              <li
                v-for="(point, index) in missionPoints"
                :key="index"
                class="mv-point"
              >
                <span class="mv-point-dot mv-point-dot--mission" aria-hidden="true"></span>
                <span>{{ $t(point) }}</span>
              </li>
            </ul>
          </div>
        </div>
      </v-col>

      <!-- Vision -->
      <v-col cols="12" md="6">
        <div
          class="mv-card mv-card--vision"
          :class="{ 'is-visible': inView }"
          style="transition-delay: 0.15s"
        >
          <div class="mv-bar mv-bar--vision" aria-hidden="true"></div>

          <div class="mv-card-inner">
            <div class="mv-icon mv-icon--vision" aria-hidden="true">
              <v-icon size="28" color="white">mdi-telescope</v-icon>
            </div>

            <p class="mv-label mv-label--vision">{{ $t('about.ourVision') }}</p>

            <h3 class="mv-title">{{ $t('about.visionDesc') }}</h3>

            <div class="mv-divider mv-divider--vision" aria-hidden="true"></div>

            <ul class="mv-points" role="list">
              <li
                v-for="(point, index) in visionPoints"
                :key="index"
                class="mv-point"
              >
                <span class="mv-point-dot mv-point-dot--vision" aria-hidden="true"></span>
                <span>{{ $t(point) }}</span>
              </li>
            </ul>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>
// ── Brand palette ─────────────────────────────────────────────────────
$blue:   #3949ab;
$purple: #7c3aed;
$red:    #e53935;

// ── Wrap ──────────────────────────────────────────────────────────────
.mv-wrap {
  padding: 40px 0;
}

// ── Card ──────────────────────────────────────────────────────────────
.mv-card {
  height: 100%;
  border-radius: 24px;
  border: 1px solid rgba($blue, 0.1);
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 4px 24px rgba($blue, 0.05);
  overflow: hidden;
  position: relative;
  opacity: 0;
  transform: translateY(32px);
  transition: opacity 0.7s ease-out, transform 0.7s ease-out, box-shadow 0.3s ease, border-color 0.3s ease;

  .theme--dark & {
    background: rgba(18, 18, 32, 0.88);
    border-color: rgba(255, 255, 255, 0.07);
  }

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  &:hover {
    box-shadow: 0 16px 48px rgba($blue, 0.12);
    border-color: rgba($blue, 0.2);

    .theme--dark & {
      box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);
    }
  }

  &--vision:hover {
    box-shadow: 0 16px 48px rgba($red, 0.1);
    border-color: rgba($red, 0.18);
  }
}

// ── Top accent bar ────────────────────────────────────────────────────
.mv-bar {
  height: 3px;
  width: 100%;

  &--mission {
    background: linear-gradient(90deg, $blue, $purple);
  }

  &--vision {
    background: linear-gradient(90deg, $purple, $red);
  }
}

// ── Inner padding ─────────────────────────────────────────────────────
.mv-card-inner {
  padding: 36px 40px 40px;

  @media (max-width: 960px) {
    padding: 28px 28px 32px;
  }

  @media (max-width: 600px) {
    padding: 24px 20px 28px;
  }
}

// ── Icon badge ────────────────────────────────────────────────────────
.mv-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);

  .mv-card:hover & {
    transform: scale(1.08) rotate(-4deg);
  }

  &--mission {
    background: linear-gradient(135deg, $blue, $purple);
    box-shadow: 0 6px 20px rgba($blue, 0.35);
  }

  &--vision {
    background: linear-gradient(135deg, $purple, $red);
    box-shadow: 0 6px 20px rgba($red, 0.28);
  }
}

// ── Section label ─────────────────────────────────────────────────────
.mv-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 12px;

  [dir='rtl'] & {
    letter-spacing: 0;
    text-transform: none;
  }

  &--mission { color: $blue; }
  &--vision  { color: $red; }
}

// ── Title ─────────────────────────────────────────────────────────────
.mv-title {
  font-size: clamp(1.2rem, 2.4vw, 1.5rem);
  font-weight: 700;
  line-height: 1.45;
  letter-spacing: -0.02em;
  color: #1a1a2e;
  margin-bottom: 24px;

  .theme--dark & {
    color: #eeeef5;
  }

  [dir='rtl'] & {
    letter-spacing: 0;
    line-height: 1.7;
  }
}

// ── Gradient divider ──────────────────────────────────────────────────
.mv-divider {
  height: 2px;
  width: 48px;
  border-radius: 2px;
  margin-bottom: 24px;

  // RTL: align to the end (right in LTR = left in RTL)
  [dir='rtl'] & {
    margin-inline-start: auto;
    margin-inline-end: 0;
  }

  &--mission { background: linear-gradient(90deg, $blue, $purple); }
  &--vision  { background: linear-gradient(90deg, $purple, $red); }
}

// ── Points list ───────────────────────────────────────────────────────
.mv-points {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.mv-point {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.65;
  color: #5a5a72;

  .theme--dark & {
    color: #9898b2;
  }

  [dir='rtl'] & {
    line-height: 1.8;
  }
}

.mv-point-dot {
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-top: 7px;

  &--mission { background: $blue; }
  &--vision  { background: $red; }
}
</style>

<script>
export default {
  data () {
    return {
      inView: false,
      missionPoints: [
        'about.missionPoint1',
        'about.missionPoint2',
        'about.missionPoint3'
      ],
      visionPoints: [
        'about.visionPoint1',
        'about.visionPoint2',
        'about.visionPoint3'
      ]
    }
  },
  mounted () {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.inView = true
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )
    observer.observe(this.$refs.wrap)
  }
}
</script>
